import React from 'react';
import { Card } from './ui/card';
import { CalendarDays, Users, Building2, Archive, ClipboardCheck } from 'lucide-react';

/**
 * Cycle-first landing grid used by Placements and Placement Documents.
 * Shows a card per active cycle with placement progress, plus an "Unassigned"
 * bucket at the bottom for legacy placements without a cycle_id.
 *
 * Props:
 *   cycles: [{ id, name, year_of_study, start_date, end_date, status,
 *              student_count, restaurant_count, restaurant_ids }]
 *   placements: [{ id, cycle_id, restaurant_id, status }]
 *   unassignedCount: number of items in the "unassigned" bucket
 *   onSelect: (cycleIdOrNull) => void  (null means "unassigned")
 *   testIdPrefix: string  data-testid prefix (e.g. 'placements-cycle')
 */
export const CyclePickerGrid = ({ cycles, placements, unassignedCount, onSelect, testIdPrefix, showAssessments = false }) => {
  const activeCycles = (cycles || []).filter(
    (c) => c.status !== 'archived' && c.status !== 'closed'
  );

  const cycleStats = (cycle) => {
    const cyclePlacements = (placements || []).filter(
      (p) => p.cycle_id === cycle.id && (p.status === 'active' || p.status === 'pending')
    );
    const totalStudents = cycle.student_count || 0;
    const placedStudents = new Set(cyclePlacements.map((p) => p.student_id)).size;
    const totalRestaurants = cycle.restaurant_count || (cycle.restaurant_ids || []).length || 0;
    const filledRestaurants = new Set(cyclePlacements.map((p) => p.restaurant_id)).size;
    return { totalStudents, placedStudents, totalRestaurants, filledRestaurants };
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-grid`}>
      {activeCycles.length === 0 && unassignedCount === 0 && (
        <Card>
          <div className="py-12 text-center text-gray-500">
            No active placement cycles. Create one from{' '}
            <span className="font-medium text-ica-navy">Placement Cycles</span> to get started.
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {activeCycles.map((cycle) => {
          const s = cycleStats(cycle);
          const studentPct = s.totalStudents > 0 ? Math.round((s.placedStudents / s.totalStudents) * 100) : 0;
          const restaurantPct = s.totalRestaurants > 0 ? Math.round((s.filledRestaurants / s.totalRestaurants) * 100) : 0;
          return (
            <button
              key={cycle.id}
              type="button"
              data-testid={`${testIdPrefix}-card-${cycle.id}`}
              onClick={() => onSelect(cycle.id)}
              className="w-full text-left group rounded-lg border border-gray-200 bg-white hover:border-ica-navy hover:shadow-md transition-all p-5 focus:outline-none focus:ring-2 focus:ring-ica-navy/40"
            >
              <div className="flex items-center gap-6">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-ica-navy text-lg truncate">{cycle.name}</span>
                    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 shrink-0 ${
                      cycle.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {cycle.status || 'draft'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{cycle.year_of_study} · {cycle.course_type || cycle.course_length}</span>
                    <span className="text-gray-300">|</span>
                    <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {cycle.start_date} → {cycle.end_date}</span>
                  </div>
                </div>

                <div className={`hidden md:grid gap-6 shrink-0 ${showAssessments ? 'grid-cols-3 w-[34rem]' : 'grid-cols-2 w-96'}`}>
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Students placed</span>
                      <span className="font-medium text-gray-800">{s.placedStudents}/{s.totalStudents}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-ica-navy transition-all" style={{ width: `${Math.min(studentPct, 100)}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Restaurants filled</span>
                      <span className="font-medium text-gray-800">{s.filledRestaurants}/{s.totalRestaurants}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-ica-gold transition-all" style={{ width: `${Math.min(restaurantPct, 100)}%` }} />
                    </div>
                  </div>
                  {showAssessments && (() => {
                    const total = (cycle.assessments_submitted || 0) + (cycle.assessments_pending || 0);
                    const pct = total > 0 ? Math.round(((cycle.assessments_submitted || 0) / total) * 100) : 0;
                    return (
                      <div data-testid={`${testIdPrefix}-assessments-${cycle.id}`}>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span className="flex items-center gap-1"><ClipboardCheck className="w-3.5 h-3.5" /> Assessments submitted</span>
                          <span className="font-medium text-gray-800">{cycle.assessments_submitted || 0}/{total}{cycle.assessments_overdue > 0 && <span className="text-red-600 ml-1" title="overdue">· {cycle.assessments_overdue} overdue</span>}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="text-xs font-medium text-ica-navy group-hover:underline shrink-0">
                  Open →
                </div>
              </div>

              {/* Mobile: stack progress bars underneath */}
              <div className="md:hidden mt-3 space-y-2">
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Students placed</span>
                    <span className="font-medium text-gray-800">{s.placedStudents}/{s.totalStudents}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-ica-navy transition-all" style={{ width: `${Math.min(studentPct, 100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Restaurants filled</span>
                    <span className="font-medium text-gray-800">{s.filledRestaurants}/{s.totalRestaurants}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-ica-gold transition-all" style={{ width: `${Math.min(restaurantPct, 100)}%` }} />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {unassignedCount > 0 && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-unassigned`}
          onClick={() => onSelect(null)}
          className="w-full flex items-center justify-between gap-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-colors px-5 py-4 text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-gray-200">
              <Archive className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <div className="font-semibold text-gray-800">Unassigned</div>
              <div className="text-xs text-gray-500">
                {unassignedCount} placement{unassignedCount === 1 ? '' : 's'} not linked to any cycle
              </div>
            </div>
          </div>
          <span className="text-sm font-medium text-gray-600 shrink-0">Review →</span>
        </button>
      )}
    </div>
  );
};

export default CyclePickerGrid;
